import React, { useEffect, useState } from "react";
import { Loader2, Banknote, PlayCircle, CheckCircle2, Send, HandCoins, Wallet, Settings2, CalendarClock, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { PageHeader, LoadingBlock, EmptyState } from "../../components/app/ui";
import { payoutApi, codApi, errorMessage, fmtDateTime } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Checkbox } from "../../components/ui/checkbox";
import AdminUserDialog from "../../components/admin/AdminUserDialog";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const BADGE = {
  pending: "bg-amber-50 text-amber-700",
  owed: "bg-red-50 text-red-700",
  processing: "bg-blue-50 text-blue-700",
  processed: "bg-emerald-50 text-emerald-700",
  collected: "bg-emerald-50 text-emerald-700",
  submitted: "bg-amber-50 text-amber-700",
  initiated: "bg-blue-50 text-blue-700",
  paid: "bg-emerald-50 text-emerald-700",
  failed: "bg-red-50 text-red-700",
};
const payeeLabel = (p) => (p.payee_type === "restaurant" ? "Restaurant" : "Driver / Delivery partner");

export default function AdminPayouts() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rowBusy, setRowBusy] = useState(null);
  const [profileUserId, setProfileUserId] = useState(null);

  const load = () => payoutApi.adminList().then(setData).catch(() => setData({ payouts: [], summary: {} }));
  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setBusy(true);
    try {
      const res = await payoutApi.adminRun();
      toast.success(`Run complete — ${res.count} payout${res.count === 1 ? "" : "s"} (${money(res.total_net)})`);
      load();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const markPaid = async (id) => {
    const mode = window.prompt("Payment mode (e.g. Bank/NEFT, UPI, RazorpayX):", "Bank/NEFT");
    if (mode === null) return;
    const reference = window.prompt("Transfer / reference (UTR) number:", "") || "";
    setRowBusy(id);
    try { await payoutApi.adminMarkProcessed(id, { mode, reference }); toast.success("Marked as paid"); load(); }
    catch (e) { toast.error(errorMessage(e)); } finally { setRowBusy(null); }
  };
  const markCollected = async (id) => {
    const mode = window.prompt("Collection mode (e.g. Cash, UPI, Bank):", "Cash");
    if (mode === null) return;
    const reference = window.prompt("Reference number (optional):", "") || "";
    setRowBusy(id);
    try { await payoutApi.adminMarkCollected(id, { mode, reference }); toast.success("Marked as collected"); load(); }
    catch (e) { toast.error(errorMessage(e)); } finally { setRowBusy(null); }
  };
  const disburse = async (id) => {
    setRowBusy(id);
    try { await payoutApi.adminDisburse(id); toast.success("Disbursed via RazorpayX"); load(); }
    catch (e) { toast.error(errorMessage(e)); } finally { setRowBusy(null); }
  };

  if (data === null) return <><PageHeader title="Payouts" /><LoadingBlock rows={4} /></>;
  const s = data.summary || {};
  const settled = (p) => p.status === "processed" || p.status === "collected";

  return (
    <>
      <PageHeader title="Payouts" description="Weekly payouts to drivers & restaurants, plus delivery-driver food COD deposits." actions={
        isAdmin ? (
          <Button onClick={runNow} disabled={busy} className="rounded-full bg-brand text-white hover:bg-brand-600" data-testid="admin-run-payouts">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PlayCircle className="mr-2 h-4 w-4" />} Run payouts now
          </Button>
        ) : null
      } />

      {isAdmin && !s.razorpayx_connected && (
        <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-3 text-sm text-amber-800" data-testid="admin-razorpayx-note">
          <strong>RazorpayX not connected.</strong> Payouts are created as statements and can be marked paid manually. Add RazorpayX keys to enable automatic bank disbursement.
        </div>
      )}

      <div className="mb-6 grid gap-4 sm:grid-cols-4">
        <div className="kt-card p-5"><p className="text-xs uppercase tracking-wider text-ink-muted">Pending to pay</p><p className="mt-1 text-2xl font-bold text-ink" data-testid="admin-pending-count">{s.pending_count || 0}</p></div>
        <div className="kt-card p-5"><p className="text-xs uppercase tracking-wider text-ink-muted">Amount to pay</p><p className="mt-1 text-2xl font-bold text-emerald-600">{money(s.pending_net)}</p></div>
        <div className="kt-card p-5"><p className="text-xs uppercase tracking-wider text-ink-muted">Owed by partners</p><p className="mt-1 text-2xl font-bold text-ink" data-testid="admin-owed-count">{s.owed_count || 0}</p></div>
        <div className="kt-card p-5"><p className="text-xs uppercase tracking-wider text-ink-muted">Amount to collect</p><p className="mt-1 text-2xl font-bold text-red-600">{money(s.owed_net)}</p></div>
      </div>

      <SettlementSchedule isAdmin={isAdmin} />

      {(!data.payouts || data.payouts.length === 0) ? (
        <EmptyState icon={Banknote} title="No payouts yet" text="Run payouts to generate weekly statements for payees with bank details." />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-surface-300" data-testid="admin-payouts-table">
          <table className="w-full text-sm">
            <thead className="bg-surface-100 text-left text-xs uppercase tracking-wider text-ink-muted">
              <tr><th className="px-4 py-3">Payee</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Balance</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Payment</th><th className="px-4 py-3">Date</th><th className="px-4 py-3 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {data.payouts.map((p) => {
                const negative = (p.net || 0) < 0;
                return (
                <tr key={p.payout_id} className="border-t border-surface-200" data-testid={`admin-payout-${p.payout_id}`}>
                  <td className="px-4 py-3 font-medium text-ink">{p.payee_user_id ? <button onClick={() => setProfileUserId(p.payee_user_id)} className="text-left font-medium text-ink hover:text-brand hover:underline" data-testid={`payout-payee-${p.payout_id}`}>{p.payee_name}</button> : p.payee_name}</td>
                  <td className="px-4 py-3 text-ink-soft">{payeeLabel(p)}</td>
                  <td className={`px-4 py-3 font-semibold ${negative ? "text-red-600" : "text-ink"}`}>
                    {negative ? `Owes ${money(Math.abs(p.net))}` : money(p.net)}
                    {p.commission > 0 && <span className="ml-1 text-xs font-normal text-ink-muted">(fee {money(p.commission)})</span>}
                  </td>
                  <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${BADGE[p.status] || BADGE.pending}`}>{p.status}</span></td>
                  <td className="px-4 py-3 text-ink-muted" data-testid={`payout-paymeta-${p.payout_id}`}>{settled(p) ? (<span className="text-[12px]">{p.payment_mode || "manual"}{p.reference ? ` · ${p.reference}` : ""}{p.processed_at ? ` · ${fmtDateTime(p.processed_at)}` : ""}</span>) : <span className="text-ink-muted/60">—</span>}</td>
                  <td className="px-4 py-3 text-ink-muted">{fmtDateTime(p.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    {isAdmin && !settled(p) && (
                      <div className="flex justify-end gap-2">
                        {negative ? (
                          <Button size="sm" className="rounded-full bg-red-600 text-white hover:bg-red-700" disabled={rowBusy === p.payout_id} onClick={() => markCollected(p.payout_id)} data-testid={`collect-partner-${p.payout_id}`}>
                            {rowBusy === p.payout_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <HandCoins className="mr-1 h-3 w-3" />} Collect from partner
                          </Button>
                        ) : (
                          <>
                            {s.razorpayx_connected && <Button size="sm" variant="outline" className="rounded-full" disabled={rowBusy === p.payout_id} onClick={() => disburse(p.payout_id)} data-testid={`disburse-${p.payout_id}`}><Send className="mr-1 h-3 w-3" /> Pay partner</Button>}
                            <Button size="sm" variant="outline" className="rounded-full" disabled={rowBusy === p.payout_id} onClick={() => markPaid(p.payout_id)} data-testid={`mark-paid-${p.payout_id}`}>{rowBusy === p.payout_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="mr-1 h-3 w-3" />} Mark paid</Button>
                          </>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      )}

      <CodDeposits isAdmin={isAdmin} />

      <AdminUserDialog userId={profileUserId} onClose={() => setProfileUserId(null)} />
    </>
  );
}

function CollectDialog({ driver, onClose, onDone }) {
  const open = !!driver;
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!driver) return;
    setLoading(true); setAmount("");
    codApi.adminOutstandingDriver(driver.driver_id)
      .then((d) => { setOrders(d.orders || []); setSelected(new Set((d.orders || []).map((o) => o.order_id))); })
      .catch((e) => { toast.error(errorMessage(e)); onClose(); })
      .finally(() => setLoading(false));
  }, [driver]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (oid) => {
    setSelected((prev) => { const n = new Set(prev); if (n.has(oid)) n.delete(oid); else n.add(oid); return n; });
    setAmount("");
  };
  const applyAmount = (val) => {
    setAmount(val);
    const target = parseFloat(val);
    if (!val || isNaN(target) || target <= 0) { setSelected(new Set(orders.map((o) => o.order_id))); return; }
    const next = new Set(); let acc = 0;
    for (const o of orders) { if (acc + o.amount > target + 0.001) break; next.add(o.order_id); acc += o.amount; }
    setSelected(next);
  };
  const selTotal = orders.filter((o) => selected.has(o.order_id)).reduce((s, o) => s + o.amount, 0);
  const total = orders.reduce((s, o) => s + o.amount, 0);
  const remaining = Math.max(0, Math.round((total - selTotal) * 100) / 100);

  const submit = async () => {
    const ids = [...selected];
    if (ids.length === 0) return toast.error("Select at least one order, or enter an amount");
    setBusy(true);
    try {
      await codApi.adminCollect(driver.driver_id, { method: "cash", order_ids: ids });
      toast.success(remaining > 0.5 ? `Collected ${money(selTotal)} — ${money(remaining)} still pending` : `Collected ${money(selTotal)} — all settled`);
      onDone();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="cod-collect-dialog">
        <DialogHeader><DialogTitle>Collect cash from {driver?.driver_name}</DialogTitle></DialogHeader>
        {loading ? (<div className="py-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-brand" /></div>) : (
          <div className="space-y-4">
            <div className="rounded-xl bg-surface-50 p-3 text-sm">
              <div className="flex justify-between"><span className="text-ink-muted">Total held</span><span className="font-semibold text-ink">{money(total)}</span></div>
              <div className="flex justify-between"><span className="text-ink-muted">Collecting now</span><span className="font-semibold text-emerald-700" data-testid="cod-collect-selected">{money(selTotal)}</span></div>
              <div className="flex justify-between"><span className="text-ink-muted">Remaining after</span><span className="font-semibold text-red-600">{money(remaining)}</span></div>
            </div>
            <div className="space-y-1.5">
              <Label>Collect by amount (optional)</Label>
              <Input value={amount} onChange={(e) => applyAmount(e.target.value.replace(/[^\d.]/g, ""))} inputMode="decimal" placeholder={`e.g. ${Math.round(total)}`} data-testid="cod-collect-amount" />
              <p className="text-xs text-ink-muted">Fills whole orders oldest-first up to this amount. Or tick specific orders below.</p>
            </div>
            <div className="max-h-56 space-y-1.5 overflow-auto rounded-xl border border-surface-200 p-2" data-testid="cod-collect-orders">
              {orders.map((o) => (
                <label key={o.order_id} className="flex items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-surface-50" data-testid={`cod-collect-order-${o.order_id}`}>
                  <Checkbox checked={selected.has(o.order_id)} onCheckedChange={() => toggle(o.order_id)} />
                  <span className="flex-1 text-sm text-ink">{o.restaurant_name || "Order"} <span className="text-ink-muted">· {o.order_id.slice(-8).toUpperCase()}</span></span>
                  <span className="text-xs text-ink-muted">{o.date ? fmtDateTime(o.date) : ""}</span>
                  <span className="text-sm font-semibold text-ink">{money(o.amount)}</span>
                </label>
              ))}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={onClose} data-testid="cod-collect-cancel">Cancel</Button>
          <Button className="rounded-full bg-emerald-600 text-white hover:bg-emerald-700" disabled={busy || loading || selected.size === 0} onClick={submit} data-testid="cod-collect-submit">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <HandCoins className="mr-2 h-4 w-4" />} Collect {money(selTotal)}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CodDeposits({ isAdmin }) {
  const [data, setData] = useState(null);
  const [outstanding, setOutstanding] = useState(null);
  const [settings, setSettings] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [rowBusy, setRowBusy] = useState(null);
  const [collectFor, setCollectFor] = useState(null);

  const load = () => {
    codApi.adminDeposits().then(setData).catch(() => setData({ deposits: [], summary: {} }));
    codApi.adminOutstanding().then(setOutstanding).catch(() => setOutstanding({ drivers: [], total_outstanding: 0 }));
    codApi.adminSettings().then((st) => { setSettings(st); setForm(st || {}); }).catch(() => setSettings({}));
  };
  useEffect(() => { load(); }, []);

  const saveSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { const st = await codApi.adminSaveSettings(form); setSettings(st); setEditing(false); toast.success("COD payment details saved"); }
    catch (err) { toast.error(errorMessage(err)); } finally { setSaving(false); }
  };
  const verify = async (id) => {
    setRowBusy(id);
    try { await codApi.adminVerify(id); toast.success("Deposit verified"); load(); }
    catch (err) { toast.error(errorMessage(err)); } finally { setRowBusy(null); }
  };

  if (data === null) return <div className="mt-10"><LoadingBlock rows={3} /></div>;
  const su = data.summary || {};
  const deposits = data.deposits || [];
  const out = outstanding || { drivers: [], total_outstanding: 0 };

  return (
    <section className="mt-12" data-testid="admin-cod-section">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-lg font-bold text-ink"><Wallet className="h-5 w-5 text-brand" /> Delivery-driver food COD deposits</h2>
        {isAdmin && <Button variant="outline" className="rounded-full" onClick={() => setEditing((v) => !v)} data-testid="cod-settings-toggle"><Settings2 className="mr-1 h-4 w-4" /> {editing ? "Close settings" : "Payment details"}</Button>}
      </div>

      {editing && isAdmin && (
        <form onSubmit={saveSettings} className="mb-6 kt-card grid gap-4 p-5 sm:grid-cols-2" data-testid="cod-settings-form">
          <div className="space-y-2"><Label>Account holder</Label><Input value={form.bank_account_holder || ""} onChange={(e) => setForm((f) => ({ ...f, bank_account_holder: e.target.value }))} data-testid="cod-holder" /></div>
          <div className="space-y-2"><Label>Bank name</Label><Input value={form.bank_name || ""} onChange={(e) => setForm((f) => ({ ...f, bank_name: e.target.value }))} data-testid="cod-bank" /></div>
          <div className="space-y-2"><Label>Account number</Label><Input value={form.account_number || ""} onChange={(e) => setForm((f) => ({ ...f, account_number: e.target.value }))} data-testid="cod-account" /></div>
          <div className="space-y-2"><Label>IFSC</Label><Input value={form.ifsc || ""} onChange={(e) => setForm((f) => ({ ...f, ifsc: e.target.value.toUpperCase() }))} data-testid="cod-ifsc" /></div>
          <div className="space-y-2"><Label>UPI ID</Label><Input value={form.upi_id || ""} onChange={(e) => setForm((f) => ({ ...f, upi_id: e.target.value }))} data-testid="cod-upi" /></div>
          <div className="space-y-2"><Label>UPI QR image URL <span className="text-ink-muted">(optional)</span></Label><Input value={form.upi_qr_url || ""} onChange={(e) => setForm((f) => ({ ...f, upi_qr_url: e.target.value }))} placeholder="https://…" data-testid="cod-qr" /></div>
          <div className="space-y-2 sm:col-span-2"><Label>Instructions</Label><Input value={form.instructions || ""} onChange={(e) => setForm((f) => ({ ...f, instructions: e.target.value }))} data-testid="cod-instructions" /></div>
          <div className="space-y-2"><Label>Remind driver when held cash exceeds (₹)</Label><Input type="number" min="0" value={form.reminder_limit ?? 2000} onChange={(e) => setForm((f) => ({ ...f, reminder_limit: Number(e.target.value || 0) }))} placeholder="2000" data-testid="cod-reminder-limit" /><p className="text-xs text-ink-muted">A daily WhatsApp nudge is sent to any driver holding more than this. Set 0 to turn off.</p></div>
          <div className="sm:col-span-2"><Button type="submit" disabled={saving} className="rounded-full bg-brand text-white hover:bg-brand-600" data-testid="cod-save-settings">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save payment details</Button></div>
        </form>
      )}

      <div className="mb-4 grid gap-4 sm:grid-cols-3">
        <div className="kt-card p-5"><p className="text-xs uppercase tracking-wider text-ink-muted">Cash with drivers now</p><p className="mt-1 text-2xl font-bold text-red-600" data-testid="cod-outstanding-total">{money(out.total_outstanding)}</p></div>
        <div className="kt-card p-5"><p className="text-xs uppercase tracking-wider text-ink-muted">Awaiting verification</p><p className="mt-1 text-2xl font-bold text-ink" data-testid="cod-submitted-count">{su.submitted_count || 0}</p></div>
        <div className="kt-card p-5"><p className="text-xs uppercase tracking-wider text-ink-muted">Amount submitted (unverified)</p><p className="mt-1 text-2xl font-bold text-amber-600">{money(su.submitted_amount)}</p></div>
      </div>

      <h3 className="mb-2 mt-6 font-semibold text-ink">Cash currently held by drivers</h3>
      {out.drivers.length === 0 ? (
        <EmptyState icon={Wallet} title="Nothing outstanding" text="No driver is currently holding undeposited food COD." />
      ) : (
        <div className="mb-8 overflow-hidden rounded-2xl border border-surface-300" data-testid="admin-cod-outstanding-table">
          <table className="w-full text-sm">
            <thead className="bg-surface-100 text-left text-xs uppercase tracking-wider text-ink-muted">
              <tr><th className="px-4 py-3">Driver</th><th className="px-4 py-3">Phone</th><th className="px-4 py-3">Cash held</th><th className="px-4 py-3">Orders</th><th className="px-4 py-3">Oldest</th>{isAdmin && <th className="px-4 py-3 text-right">Action</th>}</tr>
            </thead>
            <tbody>
              {out.drivers.map((d) => (
                <tr key={d.driver_id} className="border-t border-surface-200" data-testid={`cod-outstanding-${d.driver_id}`}>
                  <td className="px-4 py-3 font-medium text-ink">{d.driver_name}</td>
                  <td className="px-4 py-3 text-ink-soft">{d.driver_phone || "—"}</td>
                  <td className="px-4 py-3 font-semibold text-red-600">{money(d.amount)}</td>
                  <td className="px-4 py-3 text-ink-soft">{d.count}</td>
                  <td className="px-4 py-3 text-ink-muted">{d.oldest ? fmtDateTime(d.oldest) : "—"}</td>
                  {isAdmin && (
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" className="rounded-full bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => setCollectFor(d)} data-testid={`cod-collect-${d.driver_id}`}><HandCoins className="mr-1 h-3 w-3" /> Mark collected</Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3 className="mb-2 mt-6 font-semibold text-ink">Deposit history</h3>
      {deposits.length === 0 ? (
        <EmptyState icon={Wallet} title="No COD deposits yet" text="When delivery drivers deposit the food COD cash they collected, it shows here." />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-surface-300" data-testid="admin-cod-table">
          <table className="w-full text-sm">
            <thead className="bg-surface-100 text-left text-xs uppercase tracking-wider text-ink-muted">
              <tr><th className="px-4 py-3">Driver</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">Orders</th><th className="px-4 py-3">Mode</th><th className="px-4 py-3">Reference</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Date</th><th className="px-4 py-3 text-right">Action</th></tr>
            </thead>
            <tbody>
              {deposits.map((d) => (
                <tr key={d.deposit_id} className="border-t border-surface-200" data-testid={`admin-cod-${d.deposit_id}`}>
                  <td className="px-4 py-3 font-medium text-ink">{d.driver_name || d.driver_id}</td>
                  <td className="px-4 py-3 font-semibold text-ink">{money(d.amount)}</td>
                  <td className="px-4 py-3 text-ink-soft">{d.count}</td>
                  <td className="px-4 py-3 text-ink-soft capitalize">{d.method}</td>
                  <td className="px-4 py-3 text-ink-muted">
                    <div>{d.reference || "—"}</div>
                    {d.collected_by_name && <div className="text-[11px] text-ink-muted/70" data-testid={`cod-collectedby-${d.deposit_id}`}>Collected by {d.collected_by_name}</div>}
                  </td>
                  <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${BADGE[d.status] || BADGE.pending}`}>{d.status}</span></td>
                  <td className="px-4 py-3 text-ink-muted">{fmtDateTime(d.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <a href={codApi.receiptUrl(d.deposit_id)} target="_blank" rel="noreferrer" className="inline-flex items-center rounded-full border border-surface-300 px-2.5 py-1 text-xs text-ink-soft hover:bg-surface-50" data-testid={`cod-receipt-${d.deposit_id}`}><Download className="mr-1 h-3 w-3" /> Receipt</a>
                      {isAdmin && d.status === "submitted" && (
                        <Button size="sm" variant="outline" className="rounded-full" disabled={rowBusy === d.deposit_id} onClick={() => verify(d.deposit_id)} data-testid={`cod-verify-${d.deposit_id}`}>{rowBusy === d.deposit_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="mr-1 h-3 w-3" />} Verify</Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <CollectDialog driver={collectFor} onClose={() => setCollectFor(null)} onDone={() => { setCollectFor(null); load(); }} />
    </section>
  );
}

function SettlementSchedule({ isAdmin }) {
  const [cfg, setCfg] = useState(null);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  const load = () => payoutApi.adminSettings().then((d) => { setCfg(d); setText((d.bank_holidays || []).join(", ")); }).catch(() => setCfg(null));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const list = text.split(/[\s,]+/).map((x) => x.trim()).filter(Boolean);
    setSaving(true);
    try { const r = await payoutApi.adminSaveSettings({ bank_holidays: list }); toast.success("Bank holidays saved"); setText((r.bank_holidays || []).join(", ")); load(); }
    catch (err) { toast.error(errorMessage(err)); } finally { setSaving(false); }
  };

  if (!cfg) return null;
  return (
    <div className="mb-6 kt-card p-5" data-testid="settlement-schedule">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><CalendarClock className="h-5 w-5 text-brand" /><h3 className="font-semibold text-ink">Weekly settlement schedule</h3></div>
        {isAdmin && <Button variant="outline" className="rounded-full" onClick={() => setOpen((v) => !v)} data-testid="settlement-edit-toggle">{open ? "Close" : "Bank holidays"}</Button>}
      </div>
      <p className="mt-2 text-sm text-ink-soft">Drivers & restaurants are settled <strong>every Monday</strong>; if Monday is a bank holiday it's pushed to the next working day.</p>
      <p className="mt-1 text-sm text-ink-muted">This week's settlement date: <strong className="text-ink" data-testid="settlement-date">{cfg.this_week_settlement_date}</strong>{cfg.last_auto_run ? ` · last run ${fmtDateTime(cfg.last_auto_run)}` : " · not run yet"}</p>
      {open && isAdmin && (
        <form onSubmit={save} className="mt-4 space-y-2" data-testid="settlement-holidays-form">
          <Label>Bank holidays (comma-separated YYYY-MM-DD)</Label>
          <div className="flex gap-2">
            <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="2026-06-15, 2026-08-15" data-testid="settlement-holidays-input" />
            <Button type="submit" disabled={saving} className="rounded-full bg-brand text-white hover:bg-brand-600" data-testid="settlement-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
          </div>
        </form>
      )}
    </div>
  );
}
